<template>
    <div
        v-on-clickaway="closeDropdown"
        class="relative bg-white border-b border-blue-100 FilterBar"
        :class="{
            'FilterBar--hide-filters': hideFiltersUi
        }"
    >
        <div class="flex flex-wrap justify-start px-5 py-6 mx-auto FilterBar__inner">
            <button
                v-if="viewSwappable"
                class="flex items-center justify-center w-12 h-12 rounded-full"
                :class="[view === 'map' ? 'bg-blue-500 text-white' : 'text-blue-500 bg-grey-100']"
                aria-label="Map view"
                :aria-pressed="view === 'map' ? 'true' : 'false'"
                @click="$emit('update:view', 'map')"
            >
                <svg
                    class="w-5 h-5 stroke-current"
                    fill="none"
                    viewBox="0 0 14 19"
                    role="img"
                    focusable="false"
                >
                    <path
                        d="M7.006 17.958l-.004.002-.003.002a24.867 24.867 0 01-3.253-3.235C2.168 12.83.75 10.47.75 8.2.75 4.555 3.58 1.65 7 1.65c3.41 0 6.25 2.906 6.25 6.55 0 2.55-1.433 4.924-3 6.74-1.552 1.802-3.14 2.945-3.244 3.018z"
                        stroke-width="1.5"
                    />
                    <circle cx="7" cy="8.399" r="2.25" stroke-width="1.5" />
                </svg>
            </button>

            <button
                v-if="viewSwappable"
                class="flex items-center justify-center w-12 h-12 mx-2 rounded-full"
                :class="[view === 'list' ? 'bg-blue-500 text-white' : 'text-blue-500 bg-grey-100']"
                aria-label="List view"
                :aria-pressed="view === 'list' ? 'true' : 'false'"
                @click="$emit('update:view', 'list')"
            >
                <svg class="w-5 h-5" role="img" focusable="false" fill="none" viewBox="0 0 17 18">
                    <path
                        class="stroke-current"
                        stroke-width="1.5"
                        d="M6 15.149h10.5M6 9.149h10.5M6 3.149h10.5"
                    />
                    <path class="fill-current" d="M0 7.665h3v3H0zM0 13.618h3v3H0zM0 1.712h3v3H0z" />
                </svg>
            </button>

            <Searchbox
                v-show="!hideSearchBarUi"
                ref="searchbox"
                class="flex-1"
                :class="[isMobile ? 'mr-2' : 'mr-4']"
                :filter="true"
                :searchbox="!isMobile"
                :view="view"
                :default-category="defaultCategory"
                :categories="categories"
                :hide-search-bar-ui="hideSearchBarUi"
                :hide-categories-ui="hideCategoriesUi"
                :map-info="mapInfo"
                @searched="$emit('searched', $event)"
            />
            <ais-current-refinements
                v-slot="{ items }"
                :included-attributes="[
                    'services',
                    'payments',
                    'openSaturdays',
                    'openSundays',
                    'openAfter5pm',
                    'openBefore9am'
                ]"
            >
                <button
                    class="relative flex items-center justify-center w-12 h-12 border rounded-full FilterBar__filters"
                    :class="[
                        (filters && !isMobile) || (filters && isMobile)
                            ? 'bg-blue-500 text-white'
                            : 'text-blue-500 border-blue-100'
                    ]"
                    :aria-label="'Toggle Filters, ' + countRefinements(items) + ' options selected'"
                    :aria-expanded="filters"
                    @click="filters ? $emit('close') : $emit('open')"
                >
                    <svg
                        class="w-5 h-5 text-blue-300 stroke-current"
                        role="img"
                        focusable="false"
                        aria-label=""
                        fill="none"
                        viewBox="0 0 17 18"
                    >
                        <path
                            stroke-width="1.5"
                            d="M4.07 14.25h8.359M2.195 8.25h12.109M0 2.25h16.5"
                        />
                    </svg>
                    <span
                        v-if="countRefinements(items)"
                        class="absolute top-0 right-0 flex items-center justify-center w-4 h-4 font-medium text-blue-500 bg-yellow-500 rounded-full text-2xs"
                    >
                        {{ countRefinements(items) }}
                    </span>
                </button>
            </ais-current-refinements>

            <span class="hidden mx-2 my-auto text-sm text-blue-500 FilterBar__filters md:block">
                Filter by
            </span>

            <Searchbox
                v-if="isMobile && !hideSearchBarUi"
                class="w-full h-12 mt-6 FilterBar__search-bar"
                :filter="false"
                :searchbox="true"
                :view="view"
                :default-category="defaultCategory"
                :map-info="mapInfo"
                @searched="$emit('searched', $event)"
            />
        </div>

        <div
            v-show="filters && !isMobile"
            class="absolute inset-x-0 z-20 bg-white border-t border-blue-100 top-full min-h-72"
        >
            <div class="container mt-10 mb-12 filter-grid">
                <fieldset>
                    <legend class="mb-5 font-medium text-blue-500">
                        <h3>
                            Services
                        </h3>
                    </legend>
                    <ais-refinement-list
                        v-slot="{ items, refine }"
                        :transform-items="alphabetise"
                        attribute="services"
                        class="grid grid-cols-2 gap-5 lg:grid-cols-3"
                    >
                        <BaseCheckbox
                            v-for="item in items"
                            :key="item.value"
                            class="mb-4"
                            :label="item.label"
                            :value="item.value"
                            :selected="item.isRefined"
                            @checked="
                                refine(item.value);
                                trackRefinment('Services', item.value, item.isRefined);
                            "
                        />
                    </ais-refinement-list>
                </fieldset>

                <fieldset>
                    <legend class="mb-5 font-medium text-blue-500">
                        <h3>
                            Opening Hours
                        </h3>
                    </legend>
                    <ais-toggle-refinement
                        v-slot="{ value, refine }"
                        attribute="openSaturdays"
                        label="Open Saturdays"
                    >
                        <BaseCheckbox
                            class="mb-4"
                            label="Open Saturdays"
                            value="Open Saturdays"
                            :selected="value.isRefined"
                            @checked="
                                refine(value);
                                trackRefinment('Open Hours', value, value.isRefined);
                            "
                        />
                    </ais-toggle-refinement>
                    <ais-toggle-refinement
                        v-slot="{ value, refine }"
                        attribute="openSundays"
                        label="Open Sundays"
                    >
                        <BaseCheckbox
                            class="mb-4"
                            label="Open Sundays"
                            value="Open Sundays"
                            :selected="value.isRefined"
                            @checked="
                                refine(value);
                                trackRefinment('Open Hours', value, value.isRefined);
                            "
                        />
                    </ais-toggle-refinement>
                    <ais-toggle-refinement
                        v-slot="{ value, refine }"
                        attribute="openAfter5pm"
                        label="Open after 5pm"
                    >
                        <BaseCheckbox
                            class="mb-4"
                            label="Open after 5pm"
                            value="Open after 5pm"
                            :selected="value.isRefined"
                            @checked="
                                refine(value);
                                trackRefinment('Open Hours', value, value.isRefined);
                            "
                        />
                    </ais-toggle-refinement>
                    <ais-toggle-refinement
                        v-slot="{ value, refine }"
                        attribute="openBefore9am"
                        label="Open before 9am"
                    >
                        <BaseCheckbox
                            class="mb-4"
                            label="Open before 9am"
                            value="Open before 9am"
                            :selected="value.isRefined"
                            @checked="
                                refine(value);
                                trackRefinment('Open Hours', value, value.isRefined);
                            "
                        />
                    </ais-toggle-refinement>
                </fieldset>

                <fieldset>
                    <legend class="mb-5 font-medium text-blue-500">
                        <h3>
                            Payment
                        </h3>
                    </legend>
                    <ais-refinement-list
                        v-slot="{ items, refine }"
                        :transform-items="alphabetise"
                        attribute="payments"
                    >
                        <BaseCheckbox
                            v-for="item in items"
                            :key="item.value"
                            class="mb-4"
                            :label="item.label"
                            :value="item.value"
                            :selected="item.isRefined"
                            @checked="
                                refine(item.value);
                                trackRefinment('Payment', item.value, item.isRefined);
                            "
                        />
                    </ais-refinement-list>
                </fieldset>
            </div>
            <ais-clear-refinements class="text-sm text-grey-500 container -mt-4 pb-4">
                <div slot-scope="{ canRefine, refine }">
                    <button v-if="canRefine" @click.prevent="refine">
                        Clear all
                    </button>
                </div>
            </ais-clear-refinements>
        </div>
    </div>
</template>

<script>
import sortBy from 'lodash/sortBy';
import Searchbox from './Searchbox.vue';
import { mixin as clickaway } from 'vue-clickaway';

export default {
    components: {
        Searchbox
    },

    mixins: [clickaway],

    props: {
        view: {
            type: String,
            required: true
        },

        filters: {
            type: Boolean,
            required: true
        },

        categories: {
            type: Array,
            required: true
        },

        defaultCategory: {
            type: String,
            default: undefined
        },

        hideSearchBarUi: {
            type: Boolean,
            default: false
        },

        hideFiltersUi: {
            type: Boolean,
            default: false
        },

        hideCategoriesUi: {
            type: Boolean,
            default: false
        },

        viewSwappable: {
            type: Boolean,
            default: false
        },

        mapInfo: {
            type: Object,
            required: true
        }
    },

    computed: {
        isMobile() {
            return this.$mq === 'mobile';
        }
    },

    methods: {
        alphabetise(items) {
            return sortBy(items, ['label']);
        },

        countRefinements(items) {
            return items.reduce((acc, item) => (acc += item.refinements.length), 0);
        },

        closeDropdown() {
            if (!this.isMobile) this.$emit('close');
        },
        trackRefinment(section, value, isRefined) {
            // Only track when checking, not unchecking.
            if (isRefined) return;
            window.dataLayer.push({
                event: 'GTM',
                event_name: 'apply_extras_search_filter',
                content: value,
                context: section,
                location: null
            });
        }
    }
};
</script>

<style scoped>
.filter-grid {
    grid-template-columns: 6fr 2fr 2fr;
    @apply grid gap-10;
}

.FilterBar__inner {
    max-width: 1152px;
}

.FilterBar--hide-filters {
    .FilterBar__filters {
        display: none;
    }
}
</style>
